// src/rms/data/localAdapter.js
// ============================================================================
// Local (no-backend) data adapter
// ============================================================================
// Persists the whole RMS to localStorage so the app is fully usable on
// localhost with no Supabase project attached. It implements the same
// repository interface as supabaseAdapter.js, so switching backends is an env
// var change, not a rewrite.
//
// Deliberate constraints, so behaviour here does not diverge from Postgres:
//   * every collection is operator-scoped, mirroring the RLS policies
//   * the double-booking guard mirrors the rms_reservations_no_overlap
//     exclusion constraint
//   * money is integer cents throughout, as in the schema
//   * ids are uuid-shaped so seeded data can be exported into Postgres as-is
// ============================================================================

import { createSuccessResponse, createErrorResponse } from "../utils/response";

const STORAGE_KEY = "rms.db.v1";

/**
 * The stand-in identity for local development.
 *
 * Lives here rather than in AuthContext because the adapter needs it to write
 * the owner's member row, and the data layer must not import from a React
 * context. AuthContext already imports `isLocalBackend` from ./index, so this
 * direction is the one that does not create a cycle.
 */
export const LOCAL_USER_ID = "00000000-0000-4000-8000-000000000001";
export const LOCAL_USER_EMAIL = "developer@localhost";

export const COLLECTIONS = [
  "operators",
  "members",
  "locations",
  "assetGroups",
  "assets",
  "customers",
  "customerDocuments",
  "reservations",
  "reservationAddons",
  "reservationAnswers",
  "agreements",
  "usageReadings",
  "conditionReports",
  "subscriptions",
  "occupancies",
  "accessCredentials",
  "accessEvents",
  "certifications",
  "deliveries",
  "invoices",
  "payments",
  "taxRates",
  "taxGroups",
  "taxGroupRates",
  "taxCategoryDefaults",
  "availabilityBlocks",
  "operatingHours",
  "hoursExceptions",
  "addons",
  "rateRules",
  "usageFeeSchedules",
  "cancellationPolicies",
  "checkoutQuestions",
  "assetNotes",
  "assetDocuments",
  "messageThreads",
  "messages",
  "messageTemplates",
  "announcements",
  "feedback",
  "staffActions",
  "glEntries",
  "adImpressions",
  "adClicks",
  // Platform-owned ad sales (not operator-scoped)
  "advertisers",
  "insertionOrders",
  "adCampaigns",
  "adCreatives",
  "adDeliveryDaily",
  "adRateCard",
  "agreementTemplates",
  "agreementDocuments",
  // Local only, and deliberately absent from supabaseAdapter's TABLES.
  // rms_agreement_sign_tokens has RLS on with no policies and no grants: the
  // browser must never reach it, and listing it there would suggest otherwise
  // to the next person reading the map. On this backend there is no RLS and no
  // edge function, so the signing flow keeps its tokens here instead.
  "agreementSignTokens",
];

const emptyDb = () =>
  COLLECTIONS.reduce((db, name) => {
    db[name] = [];
    return db;
  }, { meta: { version: 1, activeOperatorId: null } });

// ----------------------------------------------------------------------------
// Storage
// ----------------------------------------------------------------------------

let cache = null;

function read() {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cache = raw ? { ...emptyDb(), ...JSON.parse(raw) } : emptyDb();
  } catch (err) {
    // Corrupt or unavailable storage (private mode, quota) must not brick the
    // app — fall back to an in-memory database for this session.
    console.warn("RMS local store unreadable, starting empty:", err);
    cache = emptyDb();
  }
  return cache;
}

function write(db) {
  cache = db;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
  } catch (err) {
    console.warn("RMS local store could not persist (session-only):", err);
  }
  return db;
}

/** Wipes the local database. Used by "reset demo data" in settings. */
export function resetStore() {
  cache = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    /* nothing useful to do */
  }
  return emptyDb();
}

export function exportStore() {
  return JSON.parse(JSON.stringify(read()));
}

export function importStore(db) {
  return write({ ...emptyDb(), ...db });
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

export function uuid() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  // RFC4122 v4 shape; adequate for local demo data.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const now = () => new Date().toISOString();

/**
 * Comparison operators a `where` value may carry.
 *
 * A bare value still means equality and an array still means IN, so every
 * existing caller is untouched. An OBJECT means comparisons:
 *
 *   { starts_at: { lt: iso } }                    starts before
 *   { ends_at:   { gt: iso, orNull: true } }      ends after, or never ends
 *
 * `orNull` exists for one specific reason and is worth stating plainly: an
 * open-ended booking -- a storage occupancy, anything in a continuous vertical
 * -- has ends_at NULL, and NULL fails every comparison in both SQL and
 * JavaScript. Without it, windowing a query by date would silently drop exactly
 * the bookings that are still running, which is the worst possible thing for a
 * filter whose whole job is "what is out right now" to get wrong.
 *
 * Deliberately not a general expression language. These four operators plus
 * orNull express the overlap test, which is what every caller actually wants;
 * anything more becomes a query builder nobody asked for.
 */
const COMPARATORS = {
  gt: (a, b) => a > b,
  gte: (a, b) => a >= b,
  lt: (a, b) => a < b,
  lte: (a, b) => a <= b,
};

function matchesCondition(actual, condition) {
  // Bare value: equality. Array: membership. Both predate this function.
  if (condition === null || typeof condition !== "object") return actual === condition;
  if (Array.isArray(condition)) return condition.includes(actual);

  if (actual === null || actual === undefined) return !!condition.orNull;

  return Object.entries(condition).every(([op, operand]) => {
    if (op === "orNull") return true;
    const compare = COMPARATORS[op];
    // An unknown operator must not quietly match everything: a typo would
    // widen the result set instead of failing, and the caller would never
    // know their window was ignored.
    if (!compare) throw new Error(`Unknown filter operator: ${op}`);
    return compare(actual, operand);
  });
}

function matches(row, where) {
  return Object.entries(where).every(([key, value]) => matchesCondition(row[key], value));
}

// ----------------------------------------------------------------------------
// Generic CRUD
// ----------------------------------------------------------------------------

export function list(collection, where = {}, { orderBy, direction = "asc" } = {}) {
  const db = read();
  let rows = (db[collection] || []).filter((r) => matches(r, where));

  if (orderBy) {
    rows = [...rows].sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (av > bv ? 1 : -1) * (direction === "desc" ? -1 : 1);
    });
  }
  return rows;
}

export function find(collection, id) {
  return (read()[collection] || []).find((r) => r.id === id) || null;
}

export function insert(collection, row) {
  const db = read();
  // Identity and timestamps are assigned AFTER the spread. Spreading `row` last
  // let an explicit `id: undefined` — as produced by cloning a record with
  // `{...template, id: undefined}` — overwrite the generated id with undefined,
  // silently creating unaddressable rows.
  const record = {
    ...row,
    id: row.id || uuid(),
    created_at: row.created_at || now(),
    updated_at: now(),
  };
  db[collection] = [...(db[collection] || []), record];
  write(db);
  return record;
}

export function update(collection, id, patch) {
  const db = read();
  let updated = null;
  db[collection] = (db[collection] || []).map((r) => {
    if (r.id !== id) return r;
    updated = { ...r, ...patch, updated_at: now() };
    return updated;
  });
  write(db);
  return updated;
}

export function remove(collection, id) {
  const db = read();
  const before = (db[collection] || []).length;
  db[collection] = (db[collection] || []).filter((r) => r.id !== id);
  write(db);
  return before !== db[collection].length;
}

// ----------------------------------------------------------------------------
// Domain rules that the database would otherwise enforce
// ----------------------------------------------------------------------------

/** Statuses that hold an asset. Mirrors the SQL exclusion constraint's WHERE. */
const BLOCKING_STATUSES = ["pending", "confirmed", "checked_out", "active"];

/**
 * Mirrors rms_reservations_no_overlap. An open-ended reservation (ends_at null,
 * i.e. storage occupancy) runs to infinity, so nothing can be booked after it
 * until it is closed.
 */
export function findConflictingReservation(operatorId, assetId, startsAt, endsAt, excludeId = null) {
  const start = new Date(startsAt).getTime();
  const end = endsAt ? new Date(endsAt).getTime() : Number.POSITIVE_INFINITY;

  return (
    list("reservations", { operator_id: operatorId, asset_id: assetId }).find((r) => {
      if (r.id === excludeId) return false;
      if (!BLOCKING_STATUSES.includes(r.status)) return false;
      const rStart = new Date(r.starts_at).getTime();
      const rEnd = r.ends_at ? new Date(r.ends_at).getTime() : Number.POSITIVE_INFINITY;
      // Half-open intervals [start, end) — touching ranges do not conflict.
      return rStart < end && start < rEnd;
    }) || null
  );
}

/**
 * Mirrors rms_customer_has_valid_certification. 'None'/empty always passes.
 */
export function hasValidCertification(operatorId, customerId, certificationType) {
  // "none" is the stored value on required_certification meaning the machine
  // needs no ticket at all. It is a preset option value, not display text.
  if (!certificationType || certificationType === "none") return true;
  const today = new Date().toISOString().slice(0, 10);
  return list("certifications", {
    operator_id: operatorId,
    customer_id: customerId,
    certification_type: certificationType,
    status: "verified",
  }).some((c) => !c.expires_at || c.expires_at >= today);
}

// ----------------------------------------------------------------------------
// Sequential document numbers (RES-0001, AGR-0001, INV-0001)
// ----------------------------------------------------------------------------

export function nextNumber(collection, operatorId, prefix, field) {
  const rows = list(collection, { operator_id: operatorId });
  const highest = rows.reduce((max, r) => {
    const match = String(r[field] || "").match(/(\d+)$/);
    return match ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);
  return `${prefix}-${String(highest + 1).padStart(4, "0")}`;
}

// ----------------------------------------------------------------------------
// Repository surface — the interface both adapters implement
// ----------------------------------------------------------------------------

export const localAdapter = {
  name: "local",

  /** Every business in this browser's store. See the supabase adapter. */
  async listOperatorsForUser() {
    return createSuccessResponse(read().operators || []);
  },

  async getOperatorForUser() {
    const db = read();
    const operator = db.meta.activeOperatorId
      ? find("operators", db.meta.activeOperatorId)
      : db.operators[0] || null;
    return createSuccessResponse(operator);
  },

  async setActiveOperator(operatorId) {
    const db = read();
    db.meta = { ...db.meta, activeOperatorId: operatorId };
    write(db);
    return createSuccessResponse(operatorId);
  },

  async createOperator(payload) {
    if (!payload?.vertical_key) {
      return createErrorResponse("A vertical must be selected before provisioning.");
    }
    const operator = insert("operators", {
      business_name: payload.business_name || "My Rental Business",
      vertical_key: payload.vertical_key,
      config_overrides: payload.config_overrides || {},
      timezone: payload.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      currency: payload.currency || "USD",
      country: payload.country || null,
      // Matches the column default on rms_operators so the two backends agree
      // about what a brand new business runs in.
      default_locale: payload.default_locale || "en",
      prices_include_tax: !!payload.prices_include_tax,
      plan_tier: "free",
      ads_enabled: true,
      is_active: true,
      onboarding_completed_at: null,
    });
    // The owner joins their own roster, mirroring the AFTER INSERT trigger on
    // rms_operators in 20260820090000. Done here rather than in provision()
    // for the same reason it is a trigger there: it must happen on every path
    // that creates a business, not on the one path somebody remembered.
    insert("members", {
      operator_id: operator.id,
      user_id: LOCAL_USER_ID,
      email: LOCAL_USER_EMAIL,
      display_name: null,
      role: "owner",
      status: "active",
      invited_at: new Date().toISOString(),
      accepted_at: new Date().toISOString(),
    });

    await this.setActiveOperator(operator.id);
    return createSuccessResponse(operator);
  },

  /**
   * The signed-in person's own member row.
   *
   * On this backend there is one synthetic user who is always the owner, so
   * without an override nobody would ever see the staff or bookkeeper screens
   * before they reached production. `meta.viewAsRole` is that override: it is
   * a development affordance and it is honest about being one, since with no
   * RLS and no second user there is nothing here to protect.
   */
  async getCurrentMember(operatorId) {
    const store = read();
    const member =
      store.members.find(
        (m) => m.operator_id === operatorId && m.user_id === LOCAL_USER_ID
      ) || null;

    const viewAs = store.meta?.viewAsRole;
    if (member && viewAs && viewAs !== member.role) {
      return createSuccessResponse({ ...member, role: viewAs, view_as: true });
    }
    return createSuccessResponse(member);
  },

  /** Local only. See getCurrentMember. */
  async setViewAsRole(role) {
    const store = read();
    write({ ...store, meta: { ...store.meta, viewAsRole: role || null } });
    return createSuccessResponse(role || null);
  },

  async updateOperator(operatorId, patch) {
    const updated = update("operators", operatorId, patch);
    return updated
      ? createSuccessResponse(updated)
      : createErrorResponse("Operator not found.");
  },

  // -- generic collection access ---------------------------------------------
  async list(collection, where, options) {
    return createSuccessResponse(list(collection, where, options));
  },

  async get(collection, id) {
    return createSuccessResponse(find(collection, id));
  },

  async create(collection, row) {
    return createSuccessResponse(insert(collection, row));
  },

  async update(collection, id, patch) {
    const updated = update(collection, id, patch);
    return updated ? createSuccessResponse(updated) : createErrorResponse("Record not found.");
  },

  async remove(collection, id) {
    return createSuccessResponse(remove(collection, id));
  },

  // -- rule-aware operations -------------------------------------------------
  findConflictingReservation,
  hasValidCertification,
  nextNumber,
  resetStore,
  exportStore,
  importStore,
};

export default localAdapter;
